/**
 * Settings dialog.
 *
 * Thin modal wrapper around the cloud-storage credential form plus the
 * secondary sections (re-pull, server backups, config import, password,
 * reset) so the dialog and the onboarding wizard behave identically. A save
 * takes effect on the very next request — no restart.
 */
import { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  CloudDownload,
  FileDown,
  HardDrive,
  HardDriveDownload,
  KeyRound,
  Minus,
  RotateCcw,
  Settings,
  Sparkles,
  X,
} from 'lucide-react';
import { ApiError } from '../lib/api/client.js';
import {
  type BackupRunResult,
  settingsApi,
  type AccountView,
  type StorageSettingsView,
} from '../lib/api/settings.js';
import { syncApi, type RepullReport } from '../lib/api/sync.js';
import { useOverlayScrollbar } from '../lib/ui/overlayScroll.js';
import {
  applyBackupToLocalStorage,
  notifyPreferencesChanged,
  type ImportReport,
} from '../lib/configBackup/apply.js';
import {
  decryptBackupPayload,
} from '../lib/configBackup/crypto.js';
import {
  normalizeBackupPayload,
  parseBackupFile,
  type BackupHeader,
  ConfigBackupFormatError,
  ConfigBackupPassphraseError,
} from '../lib/configBackup/bundle.js';
import { StorageCredentialForm } from './StorageCredentialForm.js';
import { PasswordChangeForm } from './PasswordChangeForm.js';
import { LoginActivitySection } from './LoginActivitySection.js';
import { LlmSettingsForm } from './LlmSettingsForm.js';
import type { LLMSettingsView } from '../lib/api/llm.js';

interface StorageSettingsDialogProps {
  initial: StorageSettingsView | null;
  /** AI Organize endpoint view; loaded alongside the storage settings. */
  llm: LLMSettingsView | null;
  /** Called after a successful save so the screen can reload its data. */
  onSaved: () => void;
  onClose: () => void;
}

/** What each exported section means on the Web (shown pre-passphrase). */
const WEB_SECTION_INFO: Record<
  string,
  { label: string; note: string; available: boolean }
> = {
  preferences: {
    label: 'Preferences',
    note: 'font size, sidebar width, setup state',
    available: true,
  },
  llm: { label: 'LLM API', note: 'not applied on Web', available: false },
  cloudSync: { label: 'Cloud Sync', note: 'this is the server — set up on the Mac', available: false },
  mcp: { label: 'MCP Servers', note: 'Mac only', available: false },
};

function sectionLabel(section: string): string {
  return WEB_SECTION_INFO[section]?.label ?? section;
}

function summaryLine(header: BackupHeader): string {
  const date = new Date(header.exportedAt);
  const when = Number.isNaN(date.getTime())
    ? header.exportedAt
    : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  const platform = header.appPlatform === 'web' ? 'Web' : 'Mac app';
  return `Exported ${when} · ${header.appVersion} · ${platform}`;
}

export function StorageSettingsDialog({ initial, llm, onSaved, onClose }: StorageSettingsDialogProps) {
  /** How many embedded forms are mid-flight; Escape/backdrop close is blocked while > 0. */
  const [busyForms, setBusyForms] = useState(0);
  // Floating capsule scrollbar (macOS overlay parity) — see lib/ui/overlayScroll.ts.
  const modalScroll = useOverlayScrollbar<HTMLDivElement>();
  const [account, setAccount] = useState<AccountView | null>(null);
  /** Data-reset (preview instances only) goes through two taps. */
  const [resetArmed, setResetArmed] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [repullBusy, setRepullBusy] = useState(false);
  const [repullReport, setRepullReport] = useState<RepullReport | null>(null);
  const [repullError, setRepullError] = useState<string | null>(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupResult, setBackupResult] = useState<BackupRunResult | null>(null);
  const [backupError, setBackupError] = useState<string | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // Config backup import (Settings → Import from Mac).
  const backupInputRef = useRef<HTMLInputElement>(null);
  const [importFileName, setImportFileName] = useState<string | null>(null);
  const [importText, setImportText] = useState<string | null>(null);
  const [importHeader, setImportHeader] = useState<BackupHeader | null>(null);
  const [importPassphrase, setImportPassphrase] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importReport, setImportReport] = useState<ImportReport | null>(null);
  const busy = busyForms > 0 || resetBusy || repullBusy || backupBusy || importBusy;
  const trackBusy = (formBusy: boolean) =>
    setBusyForms((count) => Math.max(0, count + (formBusy ? 1 : -1)));

  /** Removes every jnote.* key so a reload replays the onboarding wizard. */
  function clearLocalSetupState(): void {
    try {
      const doomed: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && key.startsWith('jnote.')) doomed.push(key);
      }
      for (const key of doomed) window.localStorage.removeItem(key);
    } catch {
      // Private mode / quota: the wizard reappears on the next visit anyway.
    }
  }

  async function runReset(): Promise<void> {
    setResetBusy(true);
    setResetError(null);
    try {
      await settingsApi.resetData();
      // The server wiped the credentials, dropped this session and cleared
      // the cookie. Drop the local setup flags, then reload: the health check
      // reports firstRun and no done marker exists, so the wizard shows.
      clearLocalSetupState();
      window.location.reload();
    } catch (error) {
      setResetError(
        error instanceof ApiError ? error.message : 'Could not reach the server.',
      );
      setResetBusy(false);
      setResetArmed(false);
    }
  }

  async function runRepull(): Promise<void> {
    setRepullBusy(true);
    setRepullError(null);
    setRepullReport(null);
    try {
      const report = await syncApi.repull();
      setRepullReport(report);
      onSaved();
    } catch (error) {
      setRepullError(
        error instanceof ApiError ? error.message : 'Could not reach the server.',
      );
    } finally {
      setRepullBusy(false);
    }
  }

  function describeRepull(report: RepullReport): string {
    const parts: string[] = [];
    if (report.import && !report.import.skipped) {
      parts.push(
        `imported ${report.import.notes} notes, ${report.import.folders} folders` +
          (report.import.attachments > 0 ? `, ${report.import.attachments} attachments` : ''),
      );
    } else if (report.import?.skipped) {
      parts.push(
        report.import.skipped === 'no-bucket-data'
          ? 'no v1 data on the bucket'
          : `v1 import skipped (${report.import.skipped})`,
      );
    } else {
      parts.push('nothing to import');
    }
    if (report.importError) parts.push(`import failed: ${report.importError}`);
    return parts.join(' · ');
  }

  async function runBackup(): Promise<void> {
    setBackupBusy(true);
    setBackupError(null);
    setBackupResult(null);
    try {
      const { backup } = await settingsApi.runBackup();
      setBackupResult(backup);
      onSaved();
    } catch (error) {
      setBackupError(
        error instanceof ApiError ? error.message : 'Could not reach the server.',
      );
    } finally {
      setBackupBusy(false);
    }
  }

  /** File pick → parse the plaintext header; the passphrase comes after. */
  async function onPickBackup(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setImportError(null);
    setImportReport(null);
    try {
      const text = await file.text();
      const { header } = parseBackupFile(text);
      setImportFileName(file.name);
      setImportText(text);
      setImportHeader(header);
      setImportPassphrase('');
    } catch (error) {
      resetImportState();
      setImportError(describeImportError(error));
    }
  }

  async function runImport(): Promise<void> {
    if (importText == null) return;
    setImportBusy(true);
    setImportError(null);
    try {
      const { envelope } = parseBackupFile(importText);
      const json = await decryptBackupPayload(envelope, importPassphrase);
      const report = applyBackupToLocalStorage(
        normalizeBackupPayload(JSON.parse(json)),
        window.innerWidth,
      );
      notifyPreferencesChanged();
      setImportReport(report);
      resetImportState(false);
    } catch (error) {
      setImportError(describeImportError(error));
    } finally {
      setImportBusy(false);
    }
  }

  function resetImportState(clearError = true): void {
    setImportFileName(null);
    setImportText(null);
    setImportHeader(null);
    setImportPassphrase('');
    if (clearError) setImportError(null);
  }

  function describeImportError(error: unknown): string {
    if (error instanceof ConfigBackupFormatError) return error.message;
    if (error instanceof ConfigBackupPassphraseError) return error.message;
    if (error instanceof Error && error.name === 'SyntaxError') {
      return 'The backup file is damaged.';
    }
    return 'Could not read the backup file.';
  }

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // Whether the login password is still the environment seed — worth a nudge.
  useEffect(() => {
    void settingsApi
      .getAccount()
      .then((result) => setAccount(result.account))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape' && !busy) onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busy, onClose]);

  return (
    <div
      className="modal-backdrop modal-backdrop-sheet"
      role="presentation"
      onClick={() => !busy && onClose()}
    >
      <div className="ob-host ob-host-modal" ref={modalScroll.hostRef}>
        <div
          className="modal modal-wide card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="storage-settings-title"
          onClick={(event) => event.stopPropagation()}
          ref={modalScroll.scrollerRef}
        >
        <div className="modal-header">
          <Settings size={15} strokeWidth={1.75} />
          <span id="storage-settings-title">Settings</span>
          <button
            type="button"
            className="icon-button"
            ref={closeRef}
            onClick={onClose}
            aria-label="Close"
            disabled={busy}
          >
            <X size={14} strokeWidth={1.75} />
          </button>
        </div>

        <div className="modal-body">
          <div className="settings-section-title">
            <HardDrive size={13} strokeWidth={1.75} />
            <span>Cloud storage</span>
          </div>
          <StorageCredentialForm
            initial={initial}
            onBusyChange={trackBusy}
            extraActions={
              <button type="button" className="button button-quiet" onClick={onClose} disabled={busy}>
                Cancel
              </button>
            }
            onSaved={() => {
              onSaved();
              onClose();
            }}
          />
        </div>

        <div className="modal-body settings-section">
          <div className="settings-section-title">
            <Sparkles size={13} strokeWidth={1.75} />
            <span>AI Organize</span>
          </div>
          <p className="modal-text">
            OpenAI-compatible endpoint. Requests are proxied by this server — the API key is
            stored here and never reaches the browser.
          </p>
          <LlmSettingsForm initial={llm} onBusyChange={trackBusy} onSaved={() => onSaved()} />
        </div>

        <div className="modal-body settings-section">
          <div className="settings-section-title">
            <CloudDownload size={13} strokeWidth={1.75} />
            <span>Re-pull from cloud</span>
          </div>

          <p className="modal-text">
            Imports any legacy v1 bucket data into the server library — a no-op once the library
            is in place. Use this after connecting storage for the first time or if the library
            looks out of date.
          </p>

          {repullError && (
            <p className="settings-status settings-status-error" role="alert">
              {repullError}
            </p>
          )}
          {repullReport && (
            <p className="settings-status" role="status">
              {describeRepull(repullReport)}
            </p>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="button button-quiet"
              onClick={() => void runRepull()}
              disabled={busy}
            >
              {repullBusy ? 'Pulling…' : 'Re-pull from cloud'}
            </button>
          </div>
        </div>

        <div className="modal-body settings-section">
          <div className="settings-section-title">
            <HardDriveDownload size={13} strokeWidth={1.75} />
            <span>Backups</span>
          </div>

          <p className="modal-text">
            The server snapshots its library database to cloud storage on a schedule. Run one now
            to verify the pipeline or before risky changes.
          </p>

          {backupError && (
            <p className="settings-status settings-status-error" role="alert">
              {backupError}
            </p>
          )}
          {backupResult && (
            <p className="settings-status" role="status">
              {backupResult.key} · {(backupResult.sizeBytes / 1024).toFixed(1)} KB
              {backupResult.deleted.length > 0
                ? ` · pruned ${backupResult.deleted.length} old snapshot(s)`
                : ''}
            </p>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="button button-quiet"
              onClick={() => void runBackup()}
              disabled={busy}
            >
              {backupBusy ? 'Backing up…' : 'Back up now'}
            </button>
          </div>
        </div>

        <div className="modal-body settings-section">
          <div className="settings-section-title">
            <FileDown size={13} strokeWidth={1.75} />
            <span>Import from Mac</span>
          </div>

          <p className="modal-text">
            Restore a backup exported from the Mac app (Settings → Backup,{' '}
            <code>.jnbundle</code>). The file stays encrypted until you enter its
            passphrase; only what the Web app supports is applied, the rest stays on
            the Mac.
          </p>

          <input
            ref={backupInputRef}
            type="file"
            accept=".jnbundle,.json,application/json"
            className="visually-hidden"
            onChange={(event) => void onPickBackup(event)}
          />

          {importError && (
            <p className="settings-status settings-status-error" role="alert">
              {importError}
            </p>
          )}

          {importReport ? (
            <div className="backup-outcome" role="status">
              {importReport.applied.length > 0 ? (
                <span className="backup-outcome-applied">
                  Applied: {importReport.applied.join(' · ')}
                </span>
              ) : (
                <span className="backup-outcome-skipped">
                  Nothing in this backup applies to the Web app.
                </span>
              )}
              {importReport.skippedPreferences.length > 0 && (
                <span className="backup-outcome-skipped">
                  Stays on the Mac: {importReport.skippedPreferences.join(', ')}.
                </span>
              )}
              {importReport.skipped.map((skipped) => (
                <span className="backup-outcome-skipped" key={skipped.section}>
                  {sectionLabel(skipped.section)} — {skipped.reason}
                </span>
              ))}
            </div>
          ) : importHeader ? (
            <div className="backup-summary">
              <span className="backup-summary-file">{importFileName}</span>
              <span className="backup-summary-meta">{summaryLine(importHeader)}</span>
              {importHeader.sections.map((section) => {
                const meta = WEB_SECTION_INFO[section];
                return (
                  <div
                    className={
                      'backup-section-row' +
                      (meta.available ? '' : ' is-unavailable')
                    }
                    key={section}
                  >
                    {meta.available ? (
                      <Check size={13} strokeWidth={1.75} />
                    ) : (
                      <Minus size={13} strokeWidth={1.75} />
                    )}
                    <span className="backup-section-label">{meta.label}</span>
                    <span className="backup-section-note">{meta.note}</span>
                  </div>
                );
              })}
              <div className="backup-passphrase-row">
                <input
                  type="password"
                  className="input"
                  placeholder="Passphrase"
                  value={importPassphrase}
                  autoComplete="off"
                  onChange={(event) => setImportPassphrase(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && importPassphrase && !busy) {
                      void runImport();
                    }
                  }}
                />
                <button
                  type="button"
                  className="button button-quiet"
                  onClick={() => void runImport()}
                  disabled={busy || importPassphrase.length === 0}
                >
                  {importBusy ? 'Importing…' : 'Import'}
                </button>
              </div>
            </div>
          ) : (
            <div className="modal-actions">
              <button
                type="button"
                className="button button-quiet"
                onClick={() => backupInputRef.current?.click()}
                disabled={busy}
              >
                Choose backup file…
              </button>
            </div>
          )}

          {(importHeader != null || importReport != null) && (
            <div className="modal-actions">
              <button
                type="button"
                className="button button-quiet"
                onClick={() => {
                  setImportReport(null);
                  resetImportState();
                }}
                disabled={busy}
              >
                {importReport != null ? 'Import another file…' : 'Choose another file…'}
              </button>
            </div>
          )}
        </div>

        <div className="modal-body settings-section">
          <div className="settings-section-title">
            <KeyRound size={13} strokeWidth={1.75} />
            <span>Login password</span>
          </div>

          <p className="modal-text">
            Changing it signs out every other device. This session stays signed in.
          </p>

          {account && !account.passwordCustomized && (
            <div className="settings-note">
              You are still using the password seeded by the server environment — set your own
              below.
            </div>
          )}

          <PasswordChangeForm onBusyChange={trackBusy} onChanged={() => undefined} />
        </div>

        <LoginActivitySection />

        {account?.dataResetEnabled && (
          <div className="modal-body settings-section settings-reset">
            <div className="settings-section-title settings-reset-title">
              <RotateCcw size={13} strokeWidth={1.75} />
              <span>Reset data</span>
            </div>

            <p className="modal-text">
              Wipes the login password and cloud storage credentials, signs out every device and
              reloads the onboarding wizard — this deployment starts over as if brand new. Notes,
              sync history and vault data are not touched.
            </p>

            {resetError && (
              <p className="settings-status settings-status-error" role="alert">
                {resetError}
              </p>
            )}

            <div className="modal-actions">
              {resetArmed ? (
                <>
                  <button
                    type="button"
                    className="button button-quiet"
                    onClick={() => setResetArmed(false)}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="button button-danger"
                    onClick={() => void runReset()}
                    disabled={busy}
                  >
                    {resetBusy ? 'Resetting…' : 'Confirm reset'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="button button-danger"
                  onClick={() => setResetArmed(true)}
                >
                  <AlertTriangle size={13} strokeWidth={1.75} />
                  Reset data…
                </button>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
